<template>
  <div>
    <div class="container-fluid">
      <div class="page-header">
        <Breadcrumbs main="Super Admin" title="Payments" />
      </div>

      <!-- Indicateurs de revenus -->
      <div class="row">
        <div class="col-xl-3 col-md-6">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1">MRR</p>
                  <h4 class="mb-0">{{ money(stats.mrr) }}</h4>
                  <small class="text-muted">Recurring, active plans</small>
                </div>
                <div class="pay-icon"><feather type="dollar-sign" size="26"></feather></div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1">Active subscriptions</p>
                  <h4 class="mb-0">{{ num(stats.activeSubscriptions) }}</h4>
                  <small class="text-muted">active + trialing</small>
                </div>
                <div class="pay-icon"><feather type="credit-card" size="26"></feather></div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card">
            <div class="card-body">
              <p class="text-muted mb-2">By status</p>
              <ul class="list-unstyled mb-0 small">
                <li class="d-flex justify-content-between py-1" v-for="row in statusRows" :key="row.status">
                  <span :class="statusClass(row.status)">{{ row.status }}</span>
                  <strong>{{ row.count }}</strong>
                </li>
                <li v-if="!statusRows.length" class="text-muted">No data.</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card">
            <div class="card-body">
              <p class="text-muted mb-2">By provider</p>
              <ul class="list-unstyled mb-0 small">
                <li class="d-flex justify-content-between py-1" v-for="row in providerRows" :key="row.provider">
                  <span class="text-capitalize">{{ row.provider }}</span>
                  <strong>{{ row.count }}</strong>
                </li>
                <li v-if="!providerRows.length" class="text-muted">No data.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Liste des abonnements -->
      <div class="card">
        <div class="card-header">
          <div class="d-flex flex-wrap justify-content-between align-items-center">
            <h5 class="mb-0">Subscriptions <small class="text-muted">({{ total }})</small></h5>
            <div class="d-flex flex-wrap align-items-center mt-2 mt-md-0">
              <b-form-select
                v-model="statusFilter"
                :options="statusOptions"
                size="sm"
                class="w-auto mr-2"
                @change="applyFilters"
              ></b-form-select>
              <b-form-select
                v-model="providerFilter"
                :options="providerOptions"
                size="sm"
                class="w-auto mr-2"
                @change="applyFilters"
              ></b-form-select>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                :disabled="loading || !total"
                @click="exportCsv"
              >
                <feather type="download" size="14" class="mr-1"></feather>Export CSV
              </button>
            </div>
          </div>
          <p class="text-danger mb-0 mt-2" v-if="actionError">{{ actionError }}</p>
        </div>
        <div class="card-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="text-muted mt-3 mb-0">Loading subscriptions...</p>
          </div>
          <div v-else-if="loadError" class="text-center py-5">
            <p class="text-danger mb-0">{{ loadError }}</p>
            <button class="btn btn-outline-primary btn-sm mt-3" @click="load">Retry</button>
          </div>
          <template v-else>
            <div class="table-responsive datatable-vue text-center">
              <b-table
                show-empty
                striped
                hover
                head-variant="light"
                bordered
                stacked="md"
                :items="subscriptions"
                :fields="fields"
              >
                <template #empty><span>No subscription matches these filters.</span></template>
                <template #cell(userEmail)="field">{{ field.item.userEmail || "—" }}</template>
                <template #cell(planName)="field">{{ field.item.planName || field.item.planId || "—" }}</template>
                <template #cell(amount)="field">{{ money(field.item.amount, field.item.currency) }}</template>
                <template #cell(status)="field">
                  <span :class="statusBadge(field.item.status)">{{ field.item.status }}</span>
                </template>
                <template #cell(provider)="field"><span class="text-capitalize">{{ field.item.provider }}</span></template>
                <template #cell(currentPeriodEnd)="field">{{ formatDate(field.item.currentPeriodEnd) }}</template>
              </b-table>
            </div>
            <b-col md="12" class="my-1 p-0 pagination-justify" v-if="total > limit">
              <b-pagination
                :value="currentPage"
                :total-rows="total"
                :per-page="limit"
                @input="onPageChange"
                class="mt-4"
              ></b-pagination>
            </b-col>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "@/utils/firestore-shim";
import { downloadCSV } from "@/utils/store-stats";

export default {
  name: "AdminPayments",
  data() {
    return {
      fields: [
        { key: "userEmail", label: "User" },
        { key: "planName", label: "Plan" },
        { key: "amount", label: "Amount" },
        { key: "status", label: "Status" },
        { key: "provider", label: "Provider" },
        { key: "currentPeriodEnd", label: "Period end" }
      ],
      statusOptions: [
        { value: "", text: "All statuses" },
        { value: "active", text: "Active" },
        { value: "trialing", text: "Trialing" },
        { value: "past_due", text: "Past due" },
        { value: "canceled", text: "Canceled" },
        { value: "incomplete", text: "Incomplete" }
      ],
      providerOptions: [
        { value: "", text: "All providers" },
        { value: "manual", text: "Manual" },
        { value: "stripe", text: "Stripe" },
        { value: "paypal", text: "PayPal" }
      ],
      stats: {},
      subscriptions: [],
      total: 0,
      limit: 25,
      offset: 0,
      currentPage: 1,
      statusFilter: "",
      providerFilter: "",
      loading: false,
      loadError: "",
      actionError: ""
    };
  },
  computed: {
    statusRows() {
      const byStatus = (this.stats || {}).subscriptionsByStatus || {};
      return Object.keys(byStatus).map((status) => ({ status, count: byStatus[status] }));
    },
    providerRows() {
      return (this.stats || {}).byProvider || [];
    }
  },
  mounted() {
    this.loadStats();
    this.load();
  },
  methods: {
    money(value, currency) {
      const symbol = String(currency || "usd").toUpperCase() === "EUR" ? "€" : "$";
      return symbol + Number(value || 0).toFixed(2);
    },
    num(value) {
      return Number(value || 0).toLocaleString();
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
    },
    statusClass(status) {
      if (status === "active" || status === "trialing") return "text-success text-capitalize";
      if (status === "past_due") return "text-warning text-capitalize";
      if (status === "canceled") return "text-danger text-capitalize";
      return "text-muted text-capitalize";
    },
    statusBadge(status) {
      if (status === "active" || status === "trialing") return "badge badge-success text-capitalize";
      if (status === "past_due") return "badge badge-warning text-capitalize";
      if (status === "canceled") return "badge badge-danger text-capitalize";
      return "badge badge-secondary text-capitalize";
    },
    queryString(extra) {
      const params = [];
      if (this.statusFilter) params.push("status=" + encodeURIComponent(this.statusFilter));
      if (this.providerFilter) params.push("provider=" + encodeURIComponent(this.providerFilter));
      params.push("limit=" + (extra && extra.limit ? extra.limit : this.limit));
      params.push("offset=" + (extra && extra.offset !== undefined ? extra.offset : this.offset));
      return params.join("&");
    },
    async loadStats() {
      try {
        const res = await firebase.api.get("/admin/stats");
        this.stats = res || {};
      } catch (e) {
        this.stats = {};
      }
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      this.actionError = "";
      try {
        const res = await firebase.api.get("/admin/subscriptions?" + this.queryString());
        this.subscriptions = (res && res.subscriptions) || [];
        this.total = (res && res.total) || 0;
      } catch (e) {
        this.loadError = "Could not load subscriptions: " + ((e && e.message) || e);
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.offset = 0;
      this.load();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.offset = (page - 1) * this.limit;
      this.load();
    },
    async exportCsv() {
      try {
        const res = await firebase.api.get("/admin/subscriptions?" + this.queryString({ limit: 10000, offset: 0 }));
        const rows = ((res && res.subscriptions) || []).map((s) => ({
          User: s.userEmail || "",
          Plan: s.planName || s.planId || "",
          Amount: Number(s.amount || 0).toFixed(2),
          Currency: s.currency || "usd",
          Status: s.status || "",
          Provider: s.provider || "",
          "Period end": s.currentPeriodEnd || "",
          Created: s.createdAt || ""
        }));
        downloadCSV(rows, "subscriptions-" + new Date().toISOString().slice(0, 10) + ".csv");
      } catch (e) {
        this.actionError = "Unable to export: " + ((e && e.message) || e);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.pay-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(40, 167, 69, 0.12);
  color: #28a745;
  flex: 0 0 auto;
}
</style>
